import * as React from 'react';
import { useCallback, useEffect, useState } from 'react';

import { AiJobStatusResponse } from '~api/ai/types';
import { personalQuizUrl } from '~api/urls';
import { useAtom, useSetAtom } from 'jotai';
import { useDispatch, useSelector } from 'react-redux';

import { translate } from '~l10n/client';
import { toast } from '~shared/components/Toast';
import { usePollHistory, useStartPoll } from '~shared/polling';
import { AIQuestionsLoadingSpinner } from '~teacher/components/QuizEditor/AIQuestionsLoadingSpinner';
import {
  CurrentQuizEditorDialog,
  currentQuizEditorDialogAtom,
} from '~teacher/components/QuizEditor/atoms/currentQuizEditorDialogAtom';
import { handleAiRateLimitError } from '~teacher/components/QuizEditor/errorHandlers/aiRateLimitErrorHandler';
import { useImportFromDoc } from '~teacher/hook/ai/useImportFromDoc';
import { workspaceKeys } from '~teacher/hook/workspaces/workspaceQueryKeyFactory';
import { queryClient } from '~teacher/queryClient';
import { loadQuizBySocNumber } from '~teacher/slices/quiz/quizAppThunks';
import { selectEditingQuiz } from '~teacher/slices/quiz/quizSelectors';
import { refreshEditingQuizQuestions } from '~teacher/slices/quiz/quizSlice';
import { proCheck } from '~teacher/util/proCheck';

import {
  extractAddedQuestionSetAtom,
  extractQuestionsPaywallAtom,
  extractQuestionsStepAtom,
} from './atoms';
import { ExtractQuestionsError } from './ExtractQuestionsError';
import { ExtractQuestionsForm } from './ExtractQuestionsForm';
import { ExtractQuestionsHeader } from './ExtractQuestionsHeader';
import { ExtractQuestionsResults } from './ExtractQuestionsResults';
import { useExtractQuestionsReducer } from './useExtractQuestionsReducer';

const MAX_FILES = 1;

interface ExtractQuestionFromDocProps {
  quizId: number;
  workspaceId: string;
  workspaceQuizId: string;
  insertionOrder: number;
}

export function ExtractQuestions({
  quizId,
  workspaceId,
  workspaceQuizId,
  insertionOrder,
}: ExtractQuestionFromDocProps) {
  const {
    state: { includeExplanation, fileKey, fileMetadata },
    dispatch,
  } = useExtractQuestionsReducer();
  const quiz = useSelector(selectEditingQuiz);
  const reduxDispatch = useDispatch();
  const [requestId, setRequestId] = useState<string | null>(null);
  const [step, setStep] = useAtom(extractQuestionsStepAtom);

  const setPaywallOpen = useSetAtom(extractQuestionsPaywallAtom);
  const setCurrentDialog = useSetAtom(currentQuizEditorDialogAtom);
  const { startPoll } = useStartPoll();

  const importFromDocMutation = useImportFromDoc({
    quizId,
    workspaceId,
    workspaceQuizId,
    insertionOrder,
  });

  const refreshQuizData = useCallback(() => {
    if (workspaceId && workspaceQuizId) {
      queryClient
        .invalidateQueries(
          workspaceKeys.byWorkspaceQuizId({
            workspaceQuizId: workspaceQuizId,
            workspaceId: workspaceId,
          })
        )
        .catch((error) => {
          console.error(
            '[ExtractQuestions] Error refreshing workspace quiz:',
            error
          );
        });
    } else if (quiz?.socNumber) {
      reduxDispatch(
        loadQuizBySocNumber({
          socNumber: quiz.socNumber,
          quizUrl: personalQuizUrl,
        })
      )
        .then(() => reduxDispatch(refreshEditingQuizQuestions({ quizId })))
        .catch((error) => {
          console.error(
            '[ExtractQuestions] Error refreshing personal quiz:',
            error
          );
        });
    }
  }, [workspaceId, workspaceQuizId, quiz?.socNumber, reduxDispatch, quizId]);

  const {
    data: pollData,
    isFailed,
    isTimeout,
    cleanup,
  } = usePollHistory<AiJobStatusResponse['data']>({
    requestId,
    onCompleted: () => {
      refreshQuizData();
    },
    cleanupOnUnmount: true,
  });

  const resetAddedQuestions = useSetAtom(extractAddedQuestionSetAtom.resetAtom);

  const handleClose = useCallback(() => {
    cleanup();
    resetAddedQuestions();
    setStep('form');
    setCurrentDialog(CurrentQuizEditorDialog.None);
  }, [cleanup, resetAddedQuestions, setCurrentDialog, setStep]);

  // Update step when questions are available
  useEffect(() => {
    if (
      step === 'loading' &&
      pollData?.questions &&
      pollData.questions.length > 0
    ) {
      setStep('results');
    }
    if (step === 'loading' && (isFailed || isTimeout)) {
      setStep('error');
    }
  }, [step, pollData, isFailed, isTimeout, setStep]);

  const handleExtract = () => {
    // Check if user has paid plan
    if (!proCheck.hasPaidPlan()) {
      setPaywallOpen(true);
      return;
    }

    if (!fileKey) {
      toast.critical(translate('Please upload a document first'));
      return;
    }

    // reset mutation before retry
    importFromDocMutation.reset();
    setStep('loading');

    importFromDocMutation.mutate(
      { fileKey, includeExplanation },
      {
        onSuccess: (data) => {
          // Backend always processes async - start polling & stay on loading step
          if (data?.async && data?.requestId) {
            setRequestId(data.requestId);

            // Pass analytics metadata to poller, which sends after completion
            startPoll({
              id: `extract-${data.requestId}`,
              type: 'ai-extract-questions',
              metadata: {
                requestId: data.requestId,
                quizId,
                workspaceId,
                workspaceQuizId,
                socNumber: quiz?.socNumber,
                analyticsMetadata: {
                  fileUploaded: !!fileKey,
                  fileUploadType: fileMetadata?.type || null,
                  fileUploadSizeBytes: fileMetadata?.size || null,
                  filesUploadedCount: MAX_FILES,
                  includeExplanations: includeExplanation,
                },
              },
            });
          }
        },
        onError: (error) => {
          handleAiRateLimitError(error);
          setStep('error');
        },
      }
    );
  };

  const handleBack = useCallback(() => {
    cleanup();
    importFromDocMutation.reset();
    setRequestId(null);
    dispatch({ type: 'removeFileKey' });
    setStep('form');
  }, [cleanup, importFromDocMutation, dispatch, setStep]);

  if (step === 'error' || importFromDocMutation.isError) {
    return <ExtractQuestionsError onRetry={handleExtract} />;
  }

  if (step === 'loading') {
    return (
      <>
        <div className="pb-4">
          <ExtractQuestionsHeader />
        </div>
        <AIQuestionsLoadingSpinner
          goBack={() => importFromDocMutation.reset()}
          isFileUpload={true}
        />
      </>
    );
  }

  if (step === 'results' && requestId) {
    return (
      <ExtractQuestionsResults
        pollData={pollData}
        isFailed={isFailed}
        isTimeout={isTimeout}
        cleanup={cleanup}
        onClose={handleClose}
        onBack={handleBack}
        refreshQuizData={refreshQuizData}
        handleExtract={handleExtract}
      />
    );
  }

  // Show form
  return (
    <ExtractQuestionsForm
      dispatch={dispatch}
      includeExplanation={includeExplanation}
      onExtract={handleExtract}
    />
  );
}
